import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Empty, message, Spin, Typography } from "antd";
import { AxiosError } from "axios";
import { ActsItem, getActById } from "entities/acts";
import { TaskActionModal } from "entities/tasks";
import {
  acceptTaskById,
  checkSignTaskById,
  declineTaskById,
  downloadTaskById,
  getTaskById,
  getTaskPdfById,
  respondTaskById,
  signTaskById,
} from "entities/tasks/api";
import { TaskActionType } from "entities/tasks/ui/task-action-modal";
import moment from "moment";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getGlobalCurrency } from "shared/lib/utils";
import ROUTES from "shared/routes";
import { SingDocument, ViewDocument } from "widgets/sign-document";
import styles from "./index.module.scss";

export const TaskPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { taskId } = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const taskActionModalRef = useRef<TaskActionType>(null);
  const [shouldPoll, setShouldPoll] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => getTaskById(taskId!),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const status = query.state.data?.edo_status?.code;
      return shouldPoll && status !== "FINISHED" ? 5000 : false;
    },
    staleTime: 0,
    gcTime: 0,
  });

  const {
    data: act,
    isLoading: isLoadingAct,
    isError: isErrorAct,
  } = useQuery({
    queryKey: ["act", data?.act_id],
    queryFn: () => getActById(data?.act_id!),
    enabled: !!data?.act_id,
  });

  const { mutate: acceptTask, isPending: isAcceptTaskPending } = useMutation({
    mutationFn: (id: string) => acceptTaskById(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      taskActionModalRef.current?.close();
      messageApi["success"](t("tasks.accept_success"));
    },
    onError(error: AxiosError<{ message: string }>) {
      messageApi["error"](
        error.response?.data?.message || t("tasks.error_occurred"),
      );
    },
  });

  const { mutate: declineTask, isPending: isDeclineTaskPending } = useMutation({
    mutationFn: (id: string) => declineTaskById(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      taskActionModalRef.current?.close();
      messageApi["success"](t("tasks.decline_success"));
    },
    onError(error: AxiosError<{ message: string }>) {
      messageApi["error"](
        error.response?.data?.message || t("tasks.error_occurred"),
      );
    },
  });

  const { mutate: respondTask, isPending: isRespondTaskPending } = useMutation({
    mutationFn: (id: string) => respondTaskById(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      taskActionModalRef.current?.close();
      messageApi["success"](t("tasks.respond_success"));
    },
    onError(error: AxiosError<{ message: string }>) {
      messageApi["error"](
        error.response?.data?.message || t("tasks.error_occurred"),
      );
    },
  });

  if (isLoading) {
    return (
      <div className={styles.wrapperLoader}>
        {contextHolder}
        <Spin />
      </div>
    );
  }

  if (isError || !data || !taskId) {
    return (
      <div className={styles.wrapper}>
        <Typography.Title level={3}>
          <Button
            type="link"
            size="large"
            icon={<ArrowLeftOutlined style={{ fontSize: "20px" }} />}
            onClick={() => navigate(ROUTES.TASKS)}
          ></Button>
          {t("tasks.title")}
        </Typography.Title>
        {contextHolder}
        <Empty
          description={
            <Typography.Text>
              {isError
                ? (error as any)?.response?.data?.message ||
                  t("tasks.error_receiving")
                : t("tasks.task_not_found")}
            </Typography.Text>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <TaskActionModal ref={taskActionModalRef} />
      {contextHolder}
      <Typography.Title level={3}>
        <Button
          type="link"
          size="large"
          icon={
            <ArrowLeftOutlined style={{ fontSize: "20px", marginRight: 8 }} />
          }
          onClick={() => navigate(ROUTES.TASKS)}
        ></Button>
        {t("tasks.task_number")} {data?.number}
        <br />
        <div
          style={{
            fontWeight: 400,
            fontSize: 14,
            padding: "6px 12px",
            borderRadius: 8,
            display: "inline-block",
            border: "1px solid var(--color-grey-light)",
          }}
        >
          {t("tasks.status_label")}{" "}
          {data.status?.code &&
            {
              DRAFT: t("tasks.status_draft"),
              SEARCH_EXECUTOR: t("tasks.status_search_executor"),
              WAIT_ANSWER: t("tasks.status_wait_answer"),
              IN_PROGRESS: t("tasks.status_in_progress"),
              COMPLETED: t("tasks.status_completed"),
              CANCELLED: t("tasks.status_cancelled"),
            }[data.status.code]}
        </div>
      </Typography.Title>
      <Typography.Title level={4}>{data.name}</Typography.Title>
      <div className={styles.info}>
        <div className={styles.infoRow}>
          <DollarOutlined className={styles.icon} />

          <div>
            <div className={styles.blockTitle}>{t("tasks.budget")}</div>
            <div className={styles.blockText}>
              {getGlobalCurrency(data.total)} {t("tasks.currency")}
            </div>
          </div>
        </div>
        <div className={styles.infoRow}>
          <CalendarOutlined className={styles.icon} />

          <div>
            <div className={styles.blockTitle}>{t("tasks.start_date")}</div>
            <div className={styles.blockText}>
              {moment(data.date_start).format("DD.MM.YYYY")} {data.time_start}
            </div>
          </div>
          {!!data.date_finish && (
            <div>
              <div className={styles.blockTitle}>{t("tasks.end_date")}</div>
              <div className={styles.blockText}>
                {moment(data.date_finish).format("DD.MM.YYYY")}{" "}
                {data.time_finish}
              </div>
            </div>
          )}
        </div>
      </div>
      <Typography.Title level={5}>{t("tasks.customer")}</Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        {data.company?.name}
      </Typography.Text>
      <Typography.Title level={5}>{t("tasks.services_list")}</Typography.Title>
      <ul className={styles.description}>
        {data.services?.map((item, index) => (
          <li key={index}>{item?.fields?.description}</li>
        ))}
      </ul>

      {!!data.place && (
        <>
          <Typography.Title level={5}>{t("tasks.place")}</Typography.Title>
          <Typography.Text style={{ color: "var(--color-grey)" }}>
            {data.place}
          </Typography.Text>
        </>
      )}
      {!!data.info && (
        <>
          <Typography.Title level={5}>{t("tasks.info")}</Typography.Title>
          <Typography.Text style={{ color: "var(--color-grey)" }}>
            {data.info}
          </Typography.Text>
        </>
      )}
      {!!data.other_data?.data?.materials && (
        <>
          <Typography.Title level={5}>
            {data.other_data?.variables?.materials}
          </Typography.Title>
          <Typography.Text
            style={{ color: "var(--color-grey)", display: "block" }}
          >
            {data.other_data?.data?.materials}
          </Typography.Text>
        </>
      )}
      {!!data.other_data?.data?.other && (
        <>
          <Typography.Title level={5}>
            {data.other_data?.variables?.other}
          </Typography.Title>
          <Typography.Text
            style={{ color: "var(--color-grey)", display: "block" }}
          >
            {data.other_data?.data?.other}
          </Typography.Text>
        </>
      )}

      {(data.edo_status?.code === "SENT" ||
        data.edo_status?.code === "FINISHED") && (
        <>
          <ViewDocument
            documentId={taskId}
            localeKey={"sign_task"}
            getDocumentPdfById={getTaskPdfById}
            downloadDocumentById={downloadTaskById}
          />
          <SingDocument
            document={data}
            localeKey={"sign_task"}
            setShouldPoll={setShouldPoll}
            signDocumentById={signTaskById}
            checkSignDocumentById={checkSignTaskById}
            withoutGoToMain
          />
        </>
      )}

      {data.act_id ? (
        <>
          {isLoadingAct ? (
            <div className={styles.wrapperLoader}>
              {contextHolder}
              <Spin />
            </div>
          ) : isErrorAct || !act ? (
            <div className={styles.wrapperLoader}>
              <Typography.Text
                style={{ color: "var(--color-grey)", textAlign: "center" }}
              >
                {contextHolder}
                {t("acts.act_not_found")}
              </Typography.Text>
            </div>
          ) : (
            <ActsItem act={act} />
          )}
        </>
      ) : (
        <div className={styles.footer}>
          {data.status?.code === "WAIT_ANSWER" ? (
            <>
              <Button
                block
                className={styles.viewButton}
                onClick={() =>
                  taskActionModalRef.current?.open({
                    title: `${t("tasks.accept_button")}`,
                    number: `${t("tasks.task_number")} ${data?.number}`,
                    price: `${getGlobalCurrency(data.total)} ${t(
                      "tasks.currency",
                    )}`,
                    action: () => acceptTask(taskId),
                  })
                }
                loading={isAcceptTaskPending}
              >
                {t("tasks.accept_button")}
              </Button>
              <Button
                block
                className={styles.viewButton}
                onClick={() =>
                  taskActionModalRef.current?.open({
                    title: `${t("tasks.decline_button")}`,
                    number: `${t("tasks.task_number")} ${data?.number}`,
                    price: `${getGlobalCurrency(data.total)} ${t(
                      "tasks.currency",
                    )}`,
                    action: () => declineTask(taskId),
                  })
                }
                loading={isDeclineTaskPending}
              >
                {t("tasks.decline_button")}
              </Button>
            </>
          ) : null}

          {data.status?.code === "SEARCH_EXECUTOR" && !data.is_responded ? (
            <Button
              block
              className={styles.viewButton}
              onClick={() =>
                taskActionModalRef.current?.open({
                  title: `${t("tasks.respond_button")}`,
                  number: `${t("tasks.task_number")} ${data?.number}`,
                  price: `${getGlobalCurrency(data.total)} ${t(
                    "tasks.currency",
                  )}`,
                  action: () => respondTask(taskId),
                })
              }
              loading={isRespondTaskPending}
            >
              {t("tasks.respond_button")}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};
